import { useMutationState } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment, ReactNode } from 'react';
import { useDragLayer } from 'react-dnd';

import { STDMapSchema } from '@/api';
import {
  StdObjects,
  StdParagraaf,
  StdSubparagraaf,
  useOrderStdParagraaf,
  useOrderStdSubparagraaf,
  useOrderStdVoorschrift,
  useStdMapId,
  useStdObjects,
} from '@/api/queries/std';
import {
  DraggableEditor,
  DraggableParagraaf,
  DraggableSubparagraaf,
  DraggableVoorschrift,
  DroppableParagraaf,
  DroppableSubparagraaf,
  DroppableVoorschrift,
} from '@/components/DraggableEditor';
import { ModalTrigger } from '@/components/Modal';
import { EASING } from '@/constants/easing';
import { SolidSpinner } from '@/icons/components';
import { StandaardtekstParagraafAdd } from '@/modals/StandaardtekstParagraafAdd';
import { StandaardtekstSubparagraafAdd } from '@/modals/StandaardtekstSubparagraafAdd';
import { StandaardtekstVoorschriftAdd } from '@/modals/StandaardtekstVoorschriftAdd';
import { twJoin } from '@/utils/tw-merge';

import { AddButton } from './-components/AddButton';
import { Paragraaf } from './-components/Paragraaf';
import { Subparagraaf } from './-components/Subparagraaf';
import { Voorschrift } from './-components/Voorschrift';
import { VoorschriftenLayout } from './-components/voorschriftenLayout';

const REORDERING_MUTATION_KEY = ['reordering'];

/**
 * Route
 */
export const Route = createFileRoute(
  '/beheer/standaardteksten/$grondslagId/$mapId/$sectieId/voorschriften'
)({
  beforeLoad: ({ context, params }) => ({
    getTitle: () => {
      const sectie = context.queryClient.getQueryData<STDMapSchema>(
        useStdMapId.getKey(params.sectieId)
      );

      if (!sectie) return;

      return `${sectie?.Naam} (voorschriften)`;
    },
  }),
  loader: async ({ params, context: { queryClient } }) => {
    const [sectie, map] = await Promise.all([
      queryClient.ensureQueryData(useStdMapId.getOptions(params.sectieId)),
      queryClient.ensureQueryData(useStdMapId.getOptions(params.mapId)),
      queryClient.ensureQueryData(
        useStdObjects.getOptions({ sectieId: params.sectieId })
      ),
    ]);

    if (!sectie || !map) throw notFound();
  },
  component: VoorschriftenComponent,
});

/**
 * Route component
 */
function VoorschriftenComponent() {
  const { sectieId, mapId, grondslagId } = Route.useParams();

  const map = useStdMapId(mapId);
  const sectie = useStdMapId(sectieId);
  const stdObjects = useStdObjects({ sectieId });

  const paragrafen = stdObjects.data?.paragrafen;

  if (!sectie.data || !stdObjects.data || !map.data || !paragrafen) return null;

  const warnings: string[] = [];

  if (map.data?.Status === 'Archief') {
    warnings.push(
      `Let op! De bovenliggende map “${map.data.Naam}” inclusief alle inhoud is onzichtbaar voor de gebruiker.`
    );
  }

  if (sectie.data?.Status === 'Archief') {
    warnings.push(
      `Let op! De bovenliggende sectie “${sectie.data.Naam}” inclusief alle inhoud is onzichtbaar voor de gebruiker.`
    );
  }

  return (
    <VoorschriftenLayout
      mapId={mapId}
      grondslagId={grondslagId}
      backLabel="Terug naar overzicht"
      testId={Route.fullPath}
      warnings={warnings}
    >
      <h1 className="style-h2 mb-2 text-gray-900">{sectie.data?.Naam}</h1>

      <DraggableEditor>
        <Paragrafen
          paragrafen={paragrafen}
          sectieId={sectieId}
          stdObjects={stdObjects.data}
        />
      </DraggableEditor>

      <ReorderingOverlay />
    </VoorschriftenLayout>
  );
}

const Paragrafen = ({
  paragrafen,
  sectieId,
  stdObjects,
}: {
  paragrafen: StdParagraaf[];
  sectieId: string;
  stdObjects: StdObjects;
}) => {
  const orderParagraaf = useOrderStdParagraaf({
    mutationKey: REORDERING_MUTATION_KEY,
  });

  const onDropParagraaf = ({
    from,
    to,
    method,
  }: {
    from: number;
    to: number;
    method: 'prepend' | 'append';
  }) => {
    if (!paragrafen) return;

    orderParagraaf.mutate({
      from,
      to,
      method,
      paragrafen,
    });
  };

  return (
    <>
      <Spacing
        addButton={
          <ModalTrigger
            modalComponent={StandaardtekstParagraafAdd}
            onOpen={() => ({
              sectieId,
              stdObjects,
              paragraafIndex: -1,
            })}
            asChild
          >
            <AddButton
              hidden={!paragrafen.length}
              data-testid={`new-paragraaf-0`}
            >
              Nieuwe paragraaf
            </AddButton>
          </ModalTrigger>
        }
        dropzone={
          <DroppableParagraaf
            method="prepend"
            index={0}
            onDrop={(from) => {
              onDropParagraaf({
                from: from.index,
                to: 0,
                method: 'prepend',
              });
            }}
          />
        }
      />

      {paragrafen.map((paragraaf, paragraafIndex) => (
        <Fragment key={`paragraaf-${paragraaf.data.ID}`}>
          <DraggableParagraaf index={paragraafIndex}>
            {({ dragHandleRef, dragPreviewRef }) => (
              <Paragraaf
                ref={dragPreviewRef}
                dragHandleRef={dragHandleRef}
                paragraafIndex={paragraafIndex}
                paragraaf={paragraaf.data}
                sectieId={sectieId}
              >
                <div className="pt-2">
                  <Subparagrafen
                    paragraaf={paragraaf}
                    paragraafIndex={paragraafIndex}
                  />
                </div>
              </Paragraaf>
            )}
          </DraggableParagraaf>

          <Spacing
            addButton={
              <ModalTrigger
                modalComponent={StandaardtekstParagraafAdd}
                onOpen={() => ({ sectieId, stdObjects, paragraafIndex })}
                asChild
              >
                <AddButton data-testid={`new-paragraaf-${paragraafIndex + 1}`}>
                  Nieuwe paragraaf
                </AddButton>
              </ModalTrigger>
            }
            dropzone={
              <DroppableParagraaf
                method="append"
                index={paragraafIndex}
                onDrop={(from) => {
                  onDropParagraaf({
                    from: from.index,
                    to: paragraafIndex,
                    method: 'append',
                  });
                }}
              />
            }
          />
        </Fragment>
      ))}
    </>
  );
};

const Subparagrafen = ({
  paragraaf,
  paragraafIndex,
}: {
  paragraaf: StdParagraaf;
  paragraafIndex: number;
}) => {
  const orderSubparagraaf = useOrderStdSubparagraaf({
    mutationKey: REORDERING_MUTATION_KEY,
  });

  const onDropSubparagraaf = ({
    from,
    to,
    method,
    subparagrafen,
  }: {
    from: number;
    to: number;
    method: 'prepend' | 'append';
    subparagrafen: StdParagraaf['subparagrafen'];
  }) => {
    orderSubparagraaf.mutate({
      from,
      to,
      method,
      subparagrafen,
    });
  };

  return (
    <>
      {paragraaf.subparagrafen.map((subparagraaf, subparagraafIndex) => (
        <Fragment key={`subparagraaf-${subparagraaf.data.ID}`}>
          <DraggableSubparagraaf path={[paragraafIndex, subparagraafIndex]}>
            {({ dragHandleRef, dragPreviewRef }) => (
              <Subparagraaf
                ref={dragPreviewRef}
                dragHandleRef={dragHandleRef}
                paragraafIndex={paragraafIndex}
                subparagraafIndex={subparagraafIndex}
                subparagraaf={subparagraaf.data}
              >
                <Voorschriften
                  subparagraaf={subparagraaf}
                  subparagraafIndex={subparagraafIndex}
                  paragraafIndex={paragraafIndex}
                  subparagrafen={paragraaf.subparagrafen}
                />
              </Subparagraaf>
            )}
          </DraggableSubparagraaf>

          <Spacing
            addButton={
              <ModalTrigger
                modalComponent={StandaardtekstSubparagraafAdd}
                onOpen={() => ({
                  paragraafId: paragraaf.data.ID ?? '',
                  subparagraafIndex,
                  subparagrafen: paragraaf.subparagrafen,
                })}
                asChild
              >
                <AddButton
                  data-testid={`new-subparagraaf-${paragraafIndex}-${subparagraafIndex + 1}`}
                >
                  Nieuwe subparagraaf
                </AddButton>
              </ModalTrigger>
            }
            dropzone={
              <DroppableSubparagraaf
                method="append"
                onDrop={(from) => {
                  onDropSubparagraaf({
                    from: from[1],
                    to: subparagraafIndex,
                    method: 'append',
                    subparagrafen: paragraaf.subparagrafen,
                  });
                }}
                path={[paragraafIndex, subparagraafIndex]}
              />
            }
          />
        </Fragment>
      ))}
    </>
  );
};

/**
 * Component for rendering a list of voorschriften
 */
const Voorschriften = ({
  subparagraaf,
  subparagraafIndex,
  paragraafIndex,
  subparagrafen,
}: {
  subparagraaf: StdSubparagraaf;
  subparagraafIndex: number;
  paragraafIndex: number;
  subparagrafen: StdSubparagraaf[];
}) => {
  const orderVoorschrift = useOrderStdVoorschrift({
    mutationKey: REORDERING_MUTATION_KEY,
  });

  const onDropVoorschrift = ({
    from,
    to,
    method,
    subparagrafen,
  }: {
    from: {
      subparagraafIndex: number;
      voorschriftIndex: number;
    };
    to: {
      subparagraafIndex: number;
      voorschriftIndex: number;
    };
    method: 'prepend' | 'append';
    subparagrafen: StdParagraaf['subparagrafen'];
  }) => {
    if (!subparagrafen) return;

    orderVoorschrift.mutate({
      from,
      to,
      method,
      subparagrafen,
    });
  };

  return (
    <>
      <Spacing
        addButton={
          <ModalTrigger
            modalComponent={StandaardtekstVoorschriftAdd}
            onOpen={() => ({
              subparagraafId: subparagraaf.data.ID ?? '',
              voorschriftIndex: 0,
              voorschriften: subparagraaf.voorschriften,
            })}
            asChild
          >
            <AddButton
              data-testid={`new-voorschrift-${paragraafIndex}-${subparagraafIndex}-0`}
            >
              Nieuw voorschrift
            </AddButton>
          </ModalTrigger>
        }
        dropzone={
          <DroppableVoorschrift
            method="prepend"
            onDrop={(from) => {
              onDropVoorschrift({
                from: {
                  subparagraafIndex,
                  voorschriftIndex: from[2],
                },
                to: {
                  subparagraafIndex,
                  voorschriftIndex: 0,
                },
                method: 'prepend',
                subparagrafen,
              });
            }}
            path={[paragraafIndex, subparagraafIndex, 0]}
          />
        }
      />

      {subparagraaf.voorschriften.map((voorschrift, voorschriftIndex) => (
        <Fragment key={`voorschrift-${voorschrift.ID}`}>
          <DraggableVoorschrift
            path={[paragraafIndex, subparagraafIndex, voorschriftIndex]}
          >
            {({ dragHandleRef, dragPreviewRef }) => (
              <Voorschrift
                ref={dragPreviewRef}
                dragHandleRef={dragHandleRef}
                key={`voorschrift-${voorschrift.ID}`}
                paragraafIndex={paragraafIndex}
                subparagraafIndex={subparagraafIndex}
                voorschriftIndex={voorschriftIndex}
                voorschrift={voorschrift}
              />
            )}
          </DraggableVoorschrift>

          <Spacing
            addButton={
              <ModalTrigger
                modalComponent={StandaardtekstVoorschriftAdd}
                onOpen={() => ({
                  subparagraafId: subparagraaf.data.ID ?? '',
                  voorschriftIndex: voorschriftIndex + 1,
                  voorschriften: subparagraaf.voorschriften,
                })}
                asChild
              >
                <AddButton
                  data-testid={`new-voorschrift-${paragraafIndex}-${subparagraafIndex}-${voorschriftIndex + 1}`}
                >
                  Nieuw voorschrift
                </AddButton>
              </ModalTrigger>
            }
            dropzone={
              <DroppableVoorschrift
                method="append"
                onDrop={(from) => {
                  onDropVoorschrift({
                    from: {
                      subparagraafIndex: from[1],
                      voorschriftIndex: from[2],
                    },
                    to: {
                      subparagraafIndex,
                      voorschriftIndex,
                    },
                    method: 'append',
                    subparagrafen,
                  });
                }}
                path={[paragraafIndex, subparagraafIndex, voorschriftIndex]}
              />
            }
          />
        </Fragment>
      ))}
    </>
  );
};

const ReorderingOverlay = () => {
  /**
   * Check if there's any autosave mutations going on.
   *
   * This is a workaround because useIsMutating breaks when adding standaardteksten voorschriften from different paragrafen.
   */
  const variables = useMutationState({
    filters: {
      mutationKey: REORDERING_MUTATION_KEY,
    },
    select: (mutation) => mutation.state.status === 'pending',
  });

  const isReordering = !!variables.find(Boolean);

  return (
    <AnimatePresence>
      {isReordering && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: EASING.cubicOut }}
          className="fixed inset-0 z-navigation-below flex items-center justify-center bg-gray-600/50"
        >
          <div className="flex size-5 items-center justify-center rounded-sm bg-white shadow-lg">
            <SolidSpinner className="animate-spin" size={17} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Spacing = ({
  addButton,
  dropzone,
}: {
  addButton?: ReactNode;
  dropzone?: ReactNode;
}) => {
  const isDragging = useDragLayer((monitor) => monitor.isDragging());

  return (
    <div className="relative h-5">
      {dropzone && (
        <div className="absolute inset-0 flex flex-wrap items-center justify-center">
          {dropzone}
        </div>
      )}

      {addButton && (
        <div
          className={twJoin(
            'absolute inset-0 flex items-center justify-center',
            isDragging && 'pointer-events-none hidden'
          )}
        >
          {addButton}
        </div>
      )}
    </div>
  );
};
